<template>
  <div class="mb-[8px] md:mb-[10px]">
    <!-- Section header -->
    <div class="group mb-[2px] flex items-center gap-[4px] md:gap-[6px]">
      <button
        v-if="section.id"
        @click="$emit('toggle-collapse')"
        class="rounded p-[2px] transition-transform hover:bg-gray-100 dark:hover:bg-gray-200"
        :class="{ '-rotate-90': section.is_collapsed }"
      >
        <svg class="h-[16px] w-[16px] text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>

      <input
        v-if="renaming"
        ref="renameInput"
        v-model="renameName"
        @blur="saveRename"
        @keydown.enter.prevent="saveRename"
        @keydown.escape="renaming = false"
        class="w-[128px] border-none bg-yellow-50 p-0 text-sm font-semibold uppercase tracking-wider text-gray-500 focus:outline-none focus:ring-0 dark:bg-gray-200"
      />
      <h3 v-else @dblclick="startRename"
        class="cursor-default text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {{ section.name }}
      </h3>
      <span class="text-xs text-gray-400">{{ tasks.length }}</span>

      <div class="flex items-center gap-[2px] opacity-0 group-hover:opacity-100">
        <button v-if="section.display_order > 0" @click="$emit('move-section-up', section.id)" class="rounded p-[2px] text-gray-400 hover:bg-gray-200" title="Move up">
          <svg class="h-[12px] w-[12px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
          </svg>
        </button>
        <button @click="$emit('move-section-down', section.id)" class="rounded p-[2px] text-gray-400 hover:bg-gray-200" title="Move down">
          <svg class="h-[12px] w-[12px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <button v-if="!section.is_default" @click="$emit('delete-section', section.id)" class="rounded p-[2px] text-red-400 hover:bg-gray-200 hover:text-red-600" title="Delete section">
          <svg class="h-[12px] w-[12px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>

    <div v-if="!section.is_collapsed">
      <draggable
        :list="localTasks"
        group="tasks"
        item-key="id"
        handle=".drag-handle"
        ghost-class="opacity-30"
        @change="onDragChange"
        class="min-h-[2px]"
      >
        <template #item="{ element }">
          <TaskItem
            :task="element"
            :projects="projects"
            :sections="allSections"
            :selected="selectedIds.has(element.id)"
            :organize-mode="organizeMode"
            @select="$emit('select-task', element.id)"
            @toggle-complete="$emit('toggle-complete', element.id)"
            @update="(id, attrs) => $emit('update-task', id, attrs)"
            @delete="(id) => $emit('delete-task', id)"
            @move-section="(id, sectionId) => $emit('move-section', id, sectionId)"
            @change-project="(id, projectId) => $emit('change-project', id, projectId)"
            @toggle-select="(id) => $emit('toggle-select', id)"
            @range-select="(id) => $emit('range-select', id)"
          />
        </template>
      </draggable>

      <NewTaskInput v-if="!organizeMode" @create="$emit('create-task', $event)" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import draggable from 'vuedraggable'
import TaskItem from './TaskItem.vue'
import NewTaskInput from './NewTaskInput.vue'

const props = defineProps({
  section: Object,
  tasks: Array,
  projects: Array,
  allSections: Array,
  selectedIds: { type: Object, default: () => new Set() },
  organizeMode: { type: Boolean, default: false },
})

const emit = defineEmits([
  'select-task', 'create-task', 'toggle-complete', 'toggle-collapse',
  'reorder', 'update-task', 'move-section', 'change-project',
  'rename-section', 'move-section-up', 'move-section-down', 'delete-section',
  'toggle-select', 'range-select',
])

const localTasks = ref([...props.tasks])
const renaming = ref(false)
const renameName = ref('')
const renameInput = ref(null)

watch(
  () => props.tasks.map(t => `${t.id}:${t.position}:${t.updated_at}`).join(),
  () => { localTasks.value = [...props.tasks] }
)

function onDragChange(evt) {
  if (evt.added || evt.moved) {
    const updates = localTasks.value.map((task, idx) => ({
      id: task.id,
      section_id: props.section.id,
      position: idx,
    }))
    emit('reorder', updates)
  }
}

function startRename() {
  renaming.value = true
  renameName.value = props.section.name
  nextTick(() => renameInput.value?.focus())
}

function saveRename() {
  const trimmed = renameName.value.trim()
  if (trimmed && trimmed !== props.section.name) {
    emit('rename-section', props.section.id, trimmed)
  }
  renaming.value = false
}
</script>
